import BubbleCommon, { TipsMapType } from '@op/components/BubbleCommon';
import { useTablePropsInfo } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import useLanguage from '@op/hooks/useLanguage';
import { useDeleteSummaryItem } from '@op/pages/AIAnalysisHub/hooks';
import {
  buildSavedAnalysisListUrl,
  type TReportOwner,
} from '@op/components/ReportPermission';
import {
  requestAiGameList,
  requestAiTaskList,
  requestAiTabState,
} from '@op/services';
import { arrayToObj } from '@op/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'tea-component';
import { useHistory } from 'umi';

export const REPEAT_SUMMARY_TASK = 'repeat_summary_task';
export type TabType =
  | 'all'
  | 'common'
  | 'repeat_summary_task'
  | 'keyword'
  | 'url'
  | 'topic'
  | 'keyword_topic';

export function useAiTaskList({
  filter,
  source,
}: {
  filter: any;
  source: TabType;
}) {
  const {
    data,
    setData,
    total,
    setTotal,
    sortBy,
    setSortBy,
    pageInfo,
    changePageInfo,
    sortFilter,
  } = useTablePropsInfo({
    defaultPageInfo: { page: 1, page_size: 10 },
    defaultSortBy: {
      by: 'create_time',
      order: 'desc',
    },
  });

  const { wrapperRequestTile, loading } = useCancelRequestHook();

  const requestFilter = useMemo(() => {
    return {
      ...filter,
      ...pageInfo,
      ...sortFilter,
      category_list: source === 'all' ? [] : [source],
    };
  }, [pageInfo, filter, sortFilter, source]);

  function getAiTaskList() {
    setTotal(0);
    setData([]);
    wrapperRequestTile(requestAiTaskList, requestFilter).then(
      (data: { list: Array<any>; total: number }) => {
        const { list, total } = data || {};

        setTotal(total);
        setData(list || []);
      },
    );
  }

  const refreshList = useCallback(() => {
    getAiTaskList();
  }, [requestFilter]);

  const { deleteCommentSummary, deleteKeywordAndUrlSummary } =
    useDeleteSummaryItem({
      finallyCallback: () => {
        changePageInfo({ ...pageInfo, page: 1 });
      },
    });

  useEffect(() => {
    getAiTaskList();
  }, [requestFilter]);

  return {
    sortBy,
    setSortBy,
    changePageInfo,
    data,
    total,
    loading,
    pageInfo,
    deleteMap: {
      keyword_topic: deleteKeywordAndUrlSummary,
      keyword: deleteKeywordAndUrlSummary,
      url: deleteKeywordAndUrlSummary,
      common: deleteCommentSummary,
    },
    refreshList,
  };
}

export function useAiTaskTabInfo({ filter }: { filter: any }) {
  const [loading, setLoading] = useState(true);
  const { formatMessage } = useLanguage();
  const history = useHistory();
  const [state, setState] = useState<{
    [key in TabType]?: {
      count: number;
      has_new?: boolean;
      tabs?: Record<string, { count: number; has_new?: boolean }>;
    };
  }>({});
  const { Generate_Repeat_Report: GENERATE_REPEAT_REPORT } =
    useGetSingePageSourceRule({
      keys: ['Generate_Repeat_Report'],
    });

  function getEmptyText(
    btnText: string,
    text = 'You can visit the [{name}] to experience AI Summary feature.',
    url = '/opinion/AIAnalysisHub/CustomAISummary',
  ) {
    const navigateUrl = url.includes('CustomAISummary')
      ? url
      : buildSavedAnalysisListUrl(url, filter?.owner as TReportOwner | undefined);

    return {
      emptyTitleText: 'There are no AI analysis tasks at the moment.',
      emptyDescText: formatMessage(text, {
        name: (
          <Button type="link" onClick={() => history.push(navigateUrl)}>
            {formatMessage(btnText)}
          </Button>
        ),
      }),
    };
  }

  const segmentOptions = useMemo(() => {
    const list: {
      id: TabType;
      text: string;
      keys: string[];
      empty: { emptyTitleText: string; emptyDescText: any };
      tips?: TipsMapType;
      children?: {
        text: string;
        id: string;
        empty: { emptyTitleText: string; emptyDescText: any };
      }[];
    }[] = [{
      id: 'all',
      text: 'All Reports',
      keys: [
        'analysis_name',
        'category',
        'create_time',
        'creator',
        'status',
        'permission',
        'action',
      ],
      empty: getEmptyText(
        'AI Analysis Hub',
        'You can visit the [{name}] to experience AI Summary, Keyword Analysis, and URL Analysis features.',
      ),
      tips: {
        en: 'This list does not include subscription reports',
        zh: '当前列表不包含例行报告',
      },
    },
    {
      id: 'repeat_summary_task',
      text: 'Subscriptions',
      keys: [
        'analysis_name',
        'last_report_generated_time',
        'creator',
        'status',
        'permission',
        'multi_action',
      ],
      empty: getEmptyText(
        'Keyword & Topic Analysis',
        undefined,
        '/opinion/AIAnalysisHub/KeywordAnalysis',
      ),
      children: [
        {
          text: 'Custom AI Summary',
          id: 'repeat_summary_task',
          empty: getEmptyText(
            'Custom AI Summary',
            'You can go to [{name}] to create a new report, and it will automatically update based on your selected schedule.',
          ),
        },
        {
          text: 'Keyword Subscriptions',
          id: 'keyword',
          empty: getEmptyText(
            'Keyword & Topic Analysis',
            undefined,
            '/opinion/AIAnalysisHub/KeywordAnalysis',
          ),
        },
        {
          text: 'Natural Language Search Subscriptions',
          id: 'topic',
          empty: getEmptyText(
            'Keyword & Topic Analysis',
            undefined,
            '/opinion/AIAnalysisHub/KeywordAnalysis',
          ),
        },
      ],
    },
    {
      id: 'common',
      text: 'Comment Summary',
      keys: ['analysis_name', 'create_time', 'creator', 'status', 'permission', 'action'],
      empty: getEmptyText('Custom AI Summary'),
    },

    {
      id: 'keyword',
      text: 'Keyword Analysis',
      keys: ['analysis_name', 'create_time', 'creator', 'status', 'permission', 'action'],
      empty: getEmptyText(
        'Keyword & Topic Analysis',
        undefined,
        '/opinion/AIAnalysisHub/KeywordAnalysis',
      ),
    },
    {
      id: 'keyword_topic',
      text: 'Natural Language Search',
      keys: ['analysis_name', 'create_time', 'creator', 'status', 'permission', 'action'],
      empty: getEmptyText(
        'Keyword & Topic Analysis',
        undefined,
        '/opinion/AIAnalysisHub/KeywordAnalysis',
      ),
    },
    {
      id: 'url',
      text: 'URL Analysis',
      keys: ['analysis_name', 'create_time', 'creator', 'status', 'permission', 'action'],
      empty: getEmptyText(
        'URL Analysis',
        undefined,
        '/opinion/AIAnalysisHub/URLAnalysis',
      ),
    }];
    return list
      .map(({ id, text, tips, children, ...props }) => {
        const { count, has_new, tabs } = state?.[id] || {};
        const getElement = ({
          count,
          text,
          has_new,
        }: {
          text: string;
          has_new?: boolean;
          count: number;
        }) => (
          <div className="dbo-inlineflex">
            <span>
              {formatMessage({ id: text })}({count || 0})
              {/* {has_new && <span>New</span>} */}
            </span>
          </div>
        );

        const data = {
          id,
          value: id,
          label: text,
          total: count || 0,
          text: tips ? (
            <BubbleCommon placement="top" showIcon={false} tipsMap={tips}>
              {getElement({ text, has_new, count: count || 0 })}
            </BubbleCommon>
          ) : (
            getElement({ text, has_new, count: count || 0 })
          ),
          children: children as any,
          ...props,
        };

        if (children && children.length > 0) {
          data.children = children
            .filter((item) =>
              GENERATE_REPEAT_REPORT ? item.id !== REPEAT_SUMMARY_TASK : true,
            )
            .map(({ id, text, empty, ...props }) => {
              const { count, has_new } = tabs?.[id] || {};

              return {
                id,
                value: id,
                label: text,
                total: count || 0,
                text: getElement({ text, has_new, count: count || 0 }),
                empty,
                ...props,
              };
            });

          if (data.children.length === 0) return null;
        }

        return data;
      })
      .filter((item) => item !== null);
  }, [formatMessage, state, filter?.owner]);

  function getAiTaskTab(noLoading = false) {
    !noLoading && setLoading(true);
    requestAiTabState(filter)
      .then((data: any) => {
        const { all_one_time_summary_count, subscriptions, ...info } =
          data || {};

        const defaultObj = {
          count: 0,
          has_new: false,
          new_report_name: '',
        };
        const {
          repeat_keyword_analysis_keyword_task,
          count,
          has_new,
          repeat_keyword_analysis_topic_task,
          repeat_summary_task,
        } = subscriptions || {};
        setState({
          ...info,
          all: { count: all_one_time_summary_count || 0 },
          repeat_summary_task: {
            count: count || 0,
            has_new,
            new_report_name: '',
            tabs: {
              repeat_summary_task: repeat_summary_task || defaultObj,
              keyword: repeat_keyword_analysis_keyword_task || defaultObj,
              topic: repeat_keyword_analysis_topic_task || defaultObj,
            },
          },
        });
      })
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function deleteToggleState() {
    getAiTaskTab(true);
  }

  useEffect(() => {
    getAiTaskTab();
  }, [filter]);

  return {
    loading,
    segmentOptions,
    deleteToggleState,
    GENERATE_REPEAT_REPORT,
  };
}

export function useAiTaskGameList({ }: {}) {
  const [gameList, setGameList] = useState([]);
  const [gameInfoMap, setGameInfoMap] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(true);

  function getAiTaskGameList() {
    setLoading(true);
    requestAiGameList({})
      .then((data: any) => {
        const list = data?.list || [];
        setGameList(list);
        setGameInfoMap(arrayToObj(list, 'edition_unified_id'));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    getAiTaskGameList();
  }, []);

  return {
    gameList,
    gameInfoMap,
    loading,
  };
}
